import { LegalLayout, CLINIC } from "../components/LegalLayout";
import { AlertTriangle, Bot, ShieldCheck, EyeOff, UserCheck } from "lucide-react";

export default function AIUsage() {
  return (
    <LegalLayout title="Política de uso de Inteligencia Artificial">
      <p className="lead text-base">
        Cumplimiento con el <b>Reglamento (UE) 2024/1689 sobre Inteligencia Artificial (AI Act)</b>,
        directrices del Ministerio de Sanidad para el uso de IA en salud (2024) y las
        recomendaciones de la Comisión Europea sobre transparencia algorítmica.
      </p>

      <h2>1. Alcance del uso de IA en {CLINIC.name}</h2>
      <p>Esta plataforma utiliza IA generativa en dos contextos:</p>
      <ul>
        <li>
          <b>Chat público de orientación</b> (accesible en <a href="/chat">/chat</a>): responde
          preguntas generales sobre salud a visitantes anónimos.
        </li>
        <li>
          <b>Asistente clínico interno</b> (opcional, únicamente accesible al doctor durante la
          consulta): sugiere posibles códigos CIE-10, resume notas SOAP y propone plantillas de
          receta. <b>Toda sugerencia requiere revisión y aprobación humana explícita antes de
          integrarse en la historia clínica.</b>
        </li>
      </ul>

      <h2>2. Modelo y proveedor</h2>
      <div className="not-prose bg-muted/40 rounded-lg p-4 my-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div className="text-muted-foreground">Modelo</div>
        <div className="font-mono">Claude Sonnet 4.5 (anthropic)</div>
        <div className="text-muted-foreground">Proveedor</div>
        <div>Anthropic (a través de Emergent Integrations)</div>
        <div className="text-muted-foreground">Región de proceso</div>
        <div>UE / EEE</div>
        <div className="text-muted-foreground">Categoría AI Act</div>
        <div>Riesgo limitado (chat orientativo, con supervisión humana)</div>
      </div>

      <h2>3. Finalidad y limitaciones</h2>

      <div className="not-prose grid sm:grid-cols-2 gap-3 my-4">
        <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
          <div className="flex items-center gap-2 font-semibold text-emerald-900 mb-2">
            <ShieldCheck className="w-5 h-5" /> Sí puede
          </div>
          <ul className="text-sm text-emerald-900 space-y-1 pl-1">
            <li>· Orientar sobre síntomas comunes.</li>
            <li>· Explicar conceptos médicos generales.</li>
            <li>· Redirigirte a un profesional cuando corresponda.</li>
            <li>· Ayudar al doctor con borradores editables.</li>
          </ul>
        </div>
        <div className="border border-rose-200 bg-rose-50 rounded-lg p-4">
          <div className="flex items-center gap-2 font-semibold text-rose-900 mb-2">
            <AlertTriangle className="w-5 h-5" /> No puede
          </div>
          <ul className="text-sm text-rose-900 space-y-1 pl-1">
            <li>· <b>Diagnosticar</b> ni sustituir a un médico.</li>
            <li>· <b>Prescribir</b> ni firmar recetas.</li>
            <li>· Interpretar pruebas de imagen o laboratorio.</li>
            <li>· Atender urgencias (llama al <b>112</b>).</li>
          </ul>
        </div>
      </div>

      <h2>4. Supervisión humana</h2>
      <div className="not-prose flex items-start gap-3 bg-primary/5 border border-primary/20 rounded-lg p-4 my-4">
        <UserCheck className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          Toda decisión clínica corresponde al <b>Dr. Dimy Laguerre</b>, colegiado
          nº {CLINIC.colegiado}. Ninguna sugerencia de la IA se aplica automáticamente en tu
          historia clínica ni en tu tratamiento. La IA es una herramienta de apoyo; la
          responsabilidad y el criterio profesional son exclusivamente humanos (AI Act Art. 14).
        </div>
      </div>

      <h2>5. Datos que procesa la IA</h2>
      <ul>
        <li>
          <b>Chat público</b>: únicamente el texto que escribes en la conversación. No se
          asocia a tu identidad si no has iniciado sesión.
        </li>
        <li>
          <b>Asistente clínico</b>: datos anónimos de la consulta actual (síntomas, edad, notas
          SOAP), sin identificadores personales. La entrada del prompt <b>NO</b> se usa para
          entrenar modelos, según acuerdo comercial con el proveedor.
        </li>
        <li>
          <b>Retención en el proveedor</b>: 30 días para auditoría de seguridad, luego eliminación
          automática. Emergent y Anthropic actúan como encargados del tratamiento con contrato
          RGPD (Art. 28).
        </li>
      </ul>

      <h2>6. Transparencia (AI Act Art. 52)</h2>
      <ul>
        <li>Cada mensaje del chat público muestra una etiqueta ⚕️ indicando que es una respuesta generada por IA.</li>
        <li>Antes de iniciar el chat, se muestra un modal con este disclaimer que debes aceptar.</li>
        <li>El registro de tu aceptación queda archivado (fecha, IP, versión del texto) para auditoría.</li>
      </ul>

      <h2>7. Sesgos y calidad</h2>
      <p>
        Ningún modelo de IA está libre de sesgos. Si notas una respuesta incorrecta, ofensiva o
        peligrosa, escríbenos a <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> con el
        session_id (aparece en la parte superior del chat) para que revisemos y ajustemos la
        política de uso.
      </p>

      <h2>8. Retirar tu consentimiento</h2>
      <p>
        Puedes cerrar el chat y borrar la marca <code>ai_chat_consent</code> desde la política de
        cookies. También puedes solicitar la eliminación de cualquier registro asociado a tu
        cuenta mediante los derechos RGPD en <a href="/app/profile">tu perfil</a>.
      </p>

      <h2>9. Contacto para asuntos relacionados con IA</h2>
      <p>
        Responsable del uso ético de la IA: <b>Dr. Dimy Laguerre</b>. Escribe a{" "}
        <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a> indicando en el asunto «Uso de IA».
      </p>

      <div className="not-prose flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4 my-6">
        <EyeOff className="w-6 h-6 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <b>Recordatorio</b>: la información que compartes con la IA no es confidencial en el
          mismo sentido que una consulta médica presencial. Evita incluir datos sensibles
          innecesarios (DNI completo, tarjeta bancaria, contraseñas).
        </div>
      </div>

      <div className="not-prose text-center mt-8 text-xs text-muted-foreground flex items-center justify-center gap-2">
        <Bot className="w-3 h-3" /> Versión del documento: 2026.02 · Última revisión: febrero 2026
      </div>
    </LegalLayout>
  );
}
